import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";

const BADGE = {
  new_request: "bg-[#d4af37]/15 text-[#d4af37] border-[#d4af37]/30",
  awaiting_photos: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  estimate_required: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  estimate_sent: "bg-amber-500/15 text-amber-300 border-amber-400/40",
  customer_approved: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  awaiting_deposit: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  scheduled: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  in_progress: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  completed: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  cancelled: "bg-zinc-500/15 text-zinc-500 border-zinc-600/30",
};

const NEXT_STATUSES = ["new_request", "awaiting_photos", "estimate_required", "estimate_sent",
  "customer_approved", "scheduled", "in_progress", "completed", "cancelled"];

function ReschedulePanel({ booking, onSubmit, onClose }) {
  const [date, setDate] = useState(booking.date);
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");

  useEffect(() => {
    if (!date) return;
    setSlots(null);
    setTime("");
    axios.get(`${API}/availability/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [date]);

  return (
    <div className="mt-3 rounded-lg border border-[#d4af37]/30 bg-[#0a0a0c] p-3" data-testid={`reschedule-panel-${booking.id}`}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Reschedule to</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          data-testid={`reschedule-date-${booking.id}`}
          className="bg-[#121216] border border-zinc-700 rounded px-3 py-2.5 text-xs text-white font-mono focus:border-[#d4af37] outline-none" />
        <select value={time} onChange={(e) => setTime(e.target.value)}
          data-testid={`reschedule-time-${booking.id}`}
          className="bg-[#121216] border border-zinc-700 rounded px-3 py-2.5 text-xs text-white font-mono focus:border-[#d4af37] outline-none">
          <option value="">{slots === null ? "Loading slots..." : slots.length ? "Pick a time" : "No slots that day"}</option>
          {(slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" disabled={!time} onClick={async () => { if (await onSubmit(booking.id, date, time)) onClose(); }}
          data-testid={`reschedule-submit-${booking.id}`}
          className="px-4 py-2.5 rounded bg-[#d4af37] text-black text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#e6ca65] transition-colors disabled:opacity-40">
          Save & Notify
        </button>
        <button type="button" onClick={onClose} data-testid={`reschedule-cancel-${booking.id}`}
          className="px-3 py-2.5 rounded border border-zinc-700 text-[10px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function AdminBookings() {
  const { logout, authHeaders } = useAuth();
  const [bookings, setBookings] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/bookings`, { headers: authHeaders() })
      .then((r) => setBookings(r.data))
      .catch((err) => { if (err.response?.status === 401) logout(); else toast.error("Could not load bookings."); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${id}`, { status }, { headers: authHeaders() });
      setBookings((p) => p.map((b) => (b.id === id ? data : b)));
      toast.success(`Booking ${status}`);
    } catch { toast.error("Update failed."); }
  };

  const confirmBooking = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${id}/confirm`, {}, { headers: authHeaders() });
      setBookings((p) => p.map((b) => (b.id === id ? data : b)));
      toast.success(data.calendar_synced
        ? "Confirmed — customer emailed and Google Calendar updated."
        : "Confirmed — customer emailed. (Google Calendar not connected)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Confirm failed.");
    }
  };

  const rescheduleBooking = async (id, date, time) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${id}/reschedule`, { date, time }, { headers: authHeaders() });
      setBookings((p) => p.map((b) => (b.id === id ? data : b)));
      toast.success("Rescheduled — customer emailed.");
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reschedule failed.");
      return false;
    }
  };

  const declineBooking = async (id) => {
    const reason = window.prompt("Reason for declining (sent to the customer — optional):", "");
    if (reason === null) return;
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${id}/decline`, { reason }, { headers: authHeaders() });
      setBookings((p) => p.map((b) => (b.id === id ? data : b)));
      toast.success("Booking declined — customer emailed.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Decline failed.");
    }
  };

  const refund = async (id) => {
    if (!window.confirm("Refund the deposit via PayPal and cancel this booking?")) return;
    try {
      await axios.post(`${API}/admin/bookings/${id}/refund`, {}, { headers: authHeaders() });
      toast.success("Deposit refunded via PayPal.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed.");
    }
  };

  if (bookings === null) return <p className="text-zinc-500 font-mono text-xs">Loading...</p>;
  if (bookings.length === 0) return <p className="text-zinc-500 font-mono text-xs py-10 text-center" data-testid="bookings-empty">No appointments yet.</p>;

  return (
    <div className="space-y-3" data-testid="bookings-panel">
      {bookings.map((b) => (
        <div key={b.id} className="rounded-lg bg-[#121216] border border-zinc-800 px-4 py-4" data-testid={`booking-row-${b.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm text-white font-medium">{b.name} — TV Mounting {b.tv_size}</p>
              <p className="text-[11px] text-[#d4af37] font-mono mt-0.5">{b.package_label}</p>
              <p className="text-[10px] text-zinc-500 font-mono mt-1">{b.date} at {b.time} · {b.address}</p>
              <p className="text-[10px] text-zinc-500 font-mono">{b.email} · {b.phone}</p>
              {(b.wall_type || b.has_mount) && (
                <p className="text-[10px] text-zinc-500 font-mono mt-0.5">
                  {b.wall_type && <span>Wall: {b.wall_type}</span>}
                  {b.wall_type && b.has_mount && " · "}
                  {b.has_mount && <span>Own mount: {b.has_mount}</span>}
                </p>
              )}
              {b.equipment?.length > 0 && (
                <p className="text-[10px] text-zinc-400 font-mono mt-0.5">Equipment: {b.equipment.join(", ")}</p>
              )}
              {b.estimate_required && (
                <p className="text-[10px] font-mono text-amber-400 mt-0.5">
                  Estimate required: {(b.complex_reasons || []).join("; ")}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="font-display text-xl text-[#d4af37]">${b.total.toLocaleString()}</p>
              <p className="text-[10px] font-mono text-zinc-500">
                deposit ${b.deposit_due} {b.paypal_capture_id ? "✓ paid" : b.deposit_due > 0 ? "unpaid" : "n/a"} · balance ${b.balance_due}
              </p>
              <span className={`inline-block mt-1 px-2.5 py-1 rounded border text-[10px] font-mono uppercase tracking-wider ${BADGE[b.status]}`}
                data-testid={`booking-status-${b.id}`}>
                {b.status.replace("_", " ")}{b.refunded_at ? " · refunded" : ""}
              </span>
              {b.calendar_event_id && (
                <p className="text-[9px] font-mono text-emerald-400 mt-1" data-testid={`booking-calendar-${b.id}`}>calendar synced</p>
              )}
            </div>
          </div>
          {b.addons?.length > 0 && (
            <p className="mt-2 text-[10px] font-mono text-zinc-400">Add-ons: {b.addons.map((a) => a.name).join(", ")}</p>
          )}
          {b.notes && <p className="mt-2 text-[11px] font-mono text-zinc-500 border-l-2 border-[#d4af37]/40 pl-2">{b.notes}</p>}
          {b.photos?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {b.photos.map((p) => (
                <a key={p.file_id} href={p.link} target="_blank" rel="noreferrer"
                  className="text-[10px] font-mono text-[#d4af37] hover:underline">↓ {p.original_filename}</a>
              ))}
            </div>
          )}
          {!["completed", "cancelled"].includes(b.status) && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`booking-actions-${b.id}`}>
              <button type="button" onClick={() => confirmBooking(b.id)} data-testid={`booking-confirm-${b.id}`}
                className="flex-1 min-w-[110px] px-3 py-2.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-300 hover:bg-emerald-500/25 transition-colors">
                Confirm
              </button>
              <button type="button" onClick={() => setReschedulingId(reschedulingId === b.id ? null : b.id)} data-testid={`booking-reschedule-${b.id}`}
                className="flex-1 min-w-[110px] px-3 py-2.5 rounded bg-sky-500/15 border border-sky-500/40 text-[10px] font-mono font-bold uppercase tracking-wider text-sky-300 hover:bg-sky-500/25 transition-colors">
                Reschedule
              </button>
              <button type="button" onClick={() => declineBooking(b.id)} data-testid={`booking-decline-${b.id}`}
                className="flex-1 min-w-[110px] px-3 py-2.5 rounded bg-red-500/10 border border-red-500/40 text-[10px] font-mono font-bold uppercase tracking-wider text-red-400 hover:bg-red-500/20 transition-colors">
                Decline
              </button>
            </div>
          )}
          {reschedulingId === b.id && (
            <ReschedulePanel booking={b} onSubmit={rescheduleBooking} onClose={() => setReschedulingId(null)} />
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">Move to</span>
            {NEXT_STATUSES.filter((s) => s !== b.status).map((s) => (
              <button key={s} type="button" onClick={() => setStatus(b.id, s)}
                data-testid={`booking-set-${s}-${b.id}`}
                className="px-2 py-1 rounded border border-zinc-700 text-[9px] font-mono uppercase tracking-wider text-zinc-400 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">
                {s.replace(/_/g, " ")}
              </button>
            ))}
            {b.paypal_capture_id && !b.refunded_at && b.status !== "cancelled" && (
              <button type="button" onClick={() => refund(b.id)} data-testid={`booking-refund-${b.id}`}
                className="px-2.5 py-1 rounded border border-amber-500/40 text-[9px] font-mono uppercase tracking-wider text-amber-400 hover:bg-amber-500/10 transition-colors">
                Refund Deposit
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
